import { Observable } from "rxjs";
import { BroadcastChannelEvent, BroadcastChannelEventTypeMap, BroadcastChannelTypes } from "ng/src/app/shared/broadcast-channels/broadcast-channels.model";
import { Utils } from "ng/src/app/core/utils";

class BroadcastChannelContainer {
	private static _instance?: BroadcastChannelContainer;
	
	static get instance() {
		return this._instance ??= new BroadcastChannelContainer();
	}
	
	private readonly channelMap = new Map<BroadcastChannelTypes, BroadcastChannelEntry<any>>();
	
	getChannel<ChannelType extends BroadcastChannelTypes>(type: ChannelType): BroadcastChannelEntry<ChannelType> {
		if (this.channelMap.has(type)) {
			return this.channelMap.get(type)!;
		}
		
		const channelEntry = new BroadcastChannelEntry(type);
		this.channelMap.set(type, channelEntry);
		return channelEntry;
	}
}

export class BroadcastChannelEntry<ChannelType extends BroadcastChannelTypes> {
	private sentEvents: Record<string, BroadcastChannelEvent<any>> = {};
	private broadcastChannel?: BroadcastChannel;
	private _listeners = 0;
	
	constructor(
		public readonly channelType: ChannelType
	) {}
	
	observe<EventTypeKey extends keyof BroadcastChannelEventTypeMap[ChannelType]>(event: EventTypeKey) {
		return new Observable<BroadcastChannelEventTypeMap[ChannelType][EventTypeKey]>((subscriber) => {
			const channel = this.getChannel();
			this._listeners += 1;
			
			const onMessage = (ev: MessageEvent<BroadcastChannelEvent<any>>) => {
				if (ev.data?.type !== event) {
					return;
				}
				else if (ev.data.key in this.sentEvents) {
					delete this.sentEvents[ev.data.key];
					return;
				}
				
				subscriber.next(ev.data as BroadcastChannelEventTypeMap[ChannelType][EventTypeKey]);
			};
			const onError = (ev: MessageEvent<any>) => {
				subscriber.error(ev);
			};
			
			channel?.addEventListener('message', onMessage);
			channel?.addEventListener('messageerror', onError);
			
			subscriber.add(() => {
				channel?.removeEventListener('message', onMessage);
				channel?.removeEventListener('messageerror', onError);
				this.processUnsubscribe();
			});
		});
	}
	
	postEvent<EventTypeKey extends keyof BroadcastChannelEventTypeMap[ChannelType]>
	(eventType: EventTypeKey, value: BroadcastChannelEventTypeMap[ChannelType][EventTypeKey]['value']) {
		const event: BroadcastChannelEvent<any> = {
			key: Utils.generateUniqueId(),
			type: eventType,
			value: value
		}
		
		this.sentEvents[event.key] = event;
		this.getChannel()?.postMessage(event);
	}
	
	private getChannel(): BroadcastChannel | undefined {
		if ('BroadcastChannel' in window) {
			return this.broadcastChannel ??= new BroadcastChannel(this.channelType);
		}
		return undefined;
	}
	
	private processUnsubscribe() {
		this._listeners -= 1;
		if (this._listeners <= 0) {
			this.broadcastChannel?.close();
			delete this.broadcastChannel;
		}
	}
}

export function getBroadcastChannel(type: BroadcastChannelTypes) {
	return BroadcastChannelContainer.instance.getChannel(type);
}